import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Asociaciones oficiales del CNI
const ASOCIACIONES_OFICIALES = [
  {
    id: 'upim',
    nombre: 'UPIM',
    nombreCompleto: 'Unión de Profesionales Inmobiliarios de México',
    logo: '/images/asociaciones/logo_upim.png',
    url: 'https://www.upim.org'
  },
  {
    id: 'amii',
    nombre: 'AMII',
    nombreCompleto: 'Asociación Mexicana de Inmobiliarias Independientes',
    logo: '/images/asociaciones/logo_amii.png',
    url: 'https://amiinacional.com'
  },
  {
    id: 'amii-bajio',
    nombre: 'AMII Bajío',
    nombreCompleto: 'AMII Bajío',
    logo: '/images/asociaciones/logo_amii_bajio.png',
    url: 'https://amiibajio.com'
  },
  {
    id: 'fiabci',
    nombre: 'FIABCI México',
    nombreCompleto: 'Federación Internacional de Profesiones Inmobiliarias',
    logo: '/images/asociaciones/logo_fiabci.png',
    url: 'https://fiabci.mx'
  },
  {
    id: 'bma',
    nombre: 'BMA',
    nombreCompleto: 'Barra Mexicana, Colegio de Abogados',
    logo: '/images/asociaciones/logo_bma.png',
    url: 'https://www.bma.org.mx'
  },
  {
    id: 'ccpm',
    nombre: 'CCPM',
    nombreCompleto: 'Colegio de Contadores Públicos de México',
    logo: '/images/asociaciones/logo_ccpm.png',
    url: 'https://www.ccpm.org.mx'
  }
];

export const AsociacionesPage = () => {
  return (
    <div className="page-transition bg-[#0a0a0a] min-h-screen" data-testid="asociaciones-page">
      {/* Hero Banner */}
      <div className="relative bg-[#111111] border-b border-[#2a2a2a]">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1637665662134-db459c1bbb46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nJTIwcm9vbSUyMGVtcHR5JTIwYm9hcmRyb29tJTIwZWxlZ2FudHxlbnwwfHx8fDE3NzA0MTI3MDF8MA&ixlib=rb-4.1.0&q=85"
            alt="Sala de juntas"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/60 to-[#111111]" />
        </div>
        <div className="container-cni relative py-16 md:py-20 text-center">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
            Organismos Aliados
          </Badge>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Asociaciones Aliadas
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            El CNI trabaja en colaboración con las principales asociaciones del 
            sector inmobiliario y profesional en México.
          </p>
        </div>
      </div>

      {/* Logos Grid - Clean & Institutional */}
      <div className="container-cni py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
          {ASOCIACIONES_OFICIALES.map((assoc) => (
            <a
              key={assoc.id}
              href={assoc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center p-6 bg-white rounded-xl border border-[#e5e5e5] hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-300"
              data-testid={`association-${assoc.id}`}
              title={assoc.nombreCompleto}
            >
              <img 
                src={assoc.logo} 
                alt={assoc.nombreCompleto}
                className="w-full h-20 object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
              />
            </a>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="container-cni pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden">
            <div className="absolute inset-0">
              <img 
                src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                alt="Acuerdo profesional"
                className="w-full h-full object-cover opacity-30"
              />
            </div>
            <div className="relative p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-manrope font-bold text-2xl text-white mb-4">
                    ¿Tu asociación no está listada?
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Si representas una asociación de profesionales inmobiliarios y deseas 
                    afiliarte al Colegio Nacional de Inmobiliarios, contáctanos para conocer 
                    los requisitos y beneficios.
                  </p>
                  <Link to="/contacto">
                    <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
                      Contactar al CNI
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="bg-[#222222] rounded-xl p-6 border border-[#333333]">
                  <h3 className="font-semibold text-white mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-amber-400" />
                    Beneficios de la Afiliación
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Validación institucional de sus miembros
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Acceso a la red profesional del CNI
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Participación en eventos y capacitaciones
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Visibilidad en el registro público
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsociacionesPage;
